import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import PageContainer from './components/common/PageContainer';
import ViewHeader from './components/ViewHeader';
import KpiRow, { KpiCell } from './components/common/KpiRow';
import StatTile from './components/common/StatTile';
import TableFooterSimple from './components/common/TableFooterSimple';
import Panel from './components/common/Panel';
import VendorsOverviewSkeleton from './Skeletons/VendorsOverviewSkeleton';
import EmptyState from './components/common/EmptyState';
import ListToolbar from './components/common/ListToolbar';
import MobileRecordCard, { KARTE_TITEL_BASIS_SX } from './components/common/MobileRecordCard';
import Pill from './components/common/Pill';
import useListQuery from './components/common/listQuery/useListQuery';
import { formatNumber, formatQty, formatUnit, formatUnitCompact } from './utils/format';
import { vendorAnzeige } from './utils/vendorAnzeige';
import { SP } from './theme/tokens';

const API = process.env.REACT_APP_API_BASE_URL || '';
const ROWS_PER_PAGE = 50;

/**
 * 🔴 G9a (UI2 Teil 10, 2026-10-20) — EINE Tabelle je Ansicht.
 *
 * Die Kopfzellen der Tabelle, die Werte der Sortier-Achse in der URL und die
 * Optionen des mobilen Sortier-Blattes kommen aus DIESER Liste. Vorher trug nur
 * der Kopf eine Liste (`{id,label,numeric}`), und das Blatt haette eine zweite
 * daneben gebraucht — zwei Quellen fuer dieselbe Ordnung, die auseinanderlaufen
 * koennen (Plan §2.1a).
 *
 * 🔴 `status` ist NICHT sortierbar (F2/B12): `Vendor` hat keine `status`-Spalte
 * (`backend/models.py`), `_vendor_to_dict` liefert dort `null`, und die Spalte
 * zeigt `rma_config.process_type`. Ein Sortierschluessel darauf waere ein
 * No-op — die Spalte bleibt lesbar, der Schluessel faellt weg.
 */
const SORT_FELDER = [ // 🔴 N9.5: `width` ist die ERKLAERTE Spaltenbreite — `tableLayout: 'fixed'` nimmt sie aus der ersten Zeile (dem Kopf). Ohne sie rechnet der Browser die Breiten aus ALLEN Zeilen; im Leerzustand fehlen die Datenzeilen, er zieht die Spalten auf die Kopfbreite zusammen, und P33 maß 181 px Versatz an Kopfzelle 1 (2026-10-20). Die Anteile sind die gemessenen Breiten des gefüllten Zustands (gemessen bei ≈ 1082 px Tabellenbreite, Reihe `xGrund` im Plan-Nachtrag vom 2026-10-21). `minWidth: 900` an der Tabelle hält die Spalten bei schmalem Desktop bedienbar. 🔴 R15-W2 (2026-10-21): die Kopfzellen trugen `nowrap` — bei 900 px sind das 126 px statt der gemessenen 150, und in fester Satzart weitet der Browser die Spalte nicht, der Text lief in die Nachbarzelle. Sie dürfen deshalb UMBRECHEN; ab ≈ 1082 px stehen sie ohnehin einzeilig.
  { id: 'anzeige', label: 'Vendor', numeric: false, sortLabel: 'Vendor', text: true, width: '28%' },
  { id: 'contact_email', label: 'Contact', numeric: false, sortLabel: 'Email', text: true, width: '19%' },
  { id: 'lead_time', label: 'Lead Time (days)', numeric: true, width: '14%' },
  { id: 'shipping_time', label: 'Shipping (days)', numeric: true, width: '13%' },
  { id: 'product_count', label: 'Products', numeric: true, width: '11%' },
  // 🔴 R16-C2 (2026-10-21): 13 % → 15 %, und der Vendor-Spalte die zwei
  // Prozent genommen (30 → 28). Anlass ist die Fotostrecke bei 1280: „manual
  // email" brach dort zweizeilig um und machte die Zeilen von Eureka und
  // Intact Idea höher als ihre Nachbarn. Die Pille bleibt jetzt einzeilig
  // (`nowrap` an den vier Aufrufstellen, die Prop des Bausteins — nicht seine
  // Vorgabe, Hausform `SecondLifeBestand.jsx:992`); die zwei Prozent geben ihr
  // den Platz dafür. Der Vendor-Name verliert ihn ohne Schaden, er trägt seit
  // dieser Runde einen Überlaufschutz (`ZELLE_KLEMME_SX`) und ist der breiteste
  // Text der Zeile. Summe unverändert 100 %.
  { id: 'status', label: 'After-sales', numeric: false, sortierbar: false, width: '15%' },
];

/** Die URL-Werte der Sortier-Achse: `feld` (aufsteigend) und `-feld` (absteigend). */
const SORT_VALUES = SORT_FELDER
  .filter((f) => f.sortierbar !== false)
  .flatMap((f) => [f.id, `-${f.id}`]);

/** Die Radiowerte des mobilen Blattes — beide Richtungen, aus derselben Liste. */
const SORT_OPTIONS = SORT_FELDER
  .filter((f) => f.sortierbar !== false)
  .flatMap((f) => [
    { value: f.id, label: `${f.sortLabel || f.label} (${f.text ? 'A–Z' : 'ascending'})` },
    { value: `-${f.id}`, label: `${f.sortLabel || f.label} (${f.text ? 'Z–A' : 'descending'})` },
  ]);

/**
 * Der Titel der mobilen Karte: ein NAME, kein Code.
 *
 * Er bricht auf hoechstens zwei Zeilen um (N11.5) — `nowrap` waere hier falsch,
 * weil ein Lieferantenname laenger ist als eine Kennung. Flaeche und 44-px-Boden
 * kommen seit R16-W4 (2026-10-21) aus `MobileRecordCard.KARTE_TITEL_BASIS_SX` —
 * EINE Quelle statt drei Kopien; hier steht nur noch die Abweichung dieser
 * Seite. Das Original stand in `POOverview.KARTE_TITEL_SX` und war woertlich
 * gleich bis auf `whiteSpace`/`overflowWrap`.
 */
const KARTE_TITEL_SX = {
  ...KARTE_TITEL_BASIS_SX,
  color: 'inherit',
  overflowWrap: 'anywhere',
};

/**
 * Überlaufschutz für die Textspalten (R16-W2 = R15-W3, 2026-10-21).
 *
 * 🔴 In fester Satzart (`tableLayout: 'fixed'`) weitet eine lange Zeichenkette
 * ihre Spalte nicht mehr — sie lief in die Nachbarzelle. Mit dem heutigen
 * Bestand war das folgenlos (die erklärten Prozente SIND die gemessene Breite
 * dieses Bestands), mit einer längeren Adresse nicht. Die Klemme kürzt sichtbar,
 * `title` trägt den vollen Text.
 *
 * ⚠️ Sie sitzt NICHT an der Zelle, sondern am Textknoten: eine `TableCell` mit
 * `textOverflow` kürzt den Inhalt ihrer Kinder nicht, wenn die ein eigener
 * Block sind. Und in einem Flex-Elternteil braucht es zusätzlich `minWidth: 0`,
 * sonst wächst das Kind über die Zelle hinaus statt zu kürzen.
 *
 * 🔴 `display: 'block'` gehört dazu, nicht nur zur Vorsicht (R16-Runde-2 C1,
 * 2026-10-21): `overflow` und `text-overflow` gelten nach CSS nur für
 * Block-Container. Zwei Träger sind `Typography variant="caption"` — MUI bildet
 * das auf `<span>` ab, und das Theme überschreibt `MuiTypography` nicht. Ohne
 * `display` bliebe die Klemme an genau den zwei Zeilen wirkungslos, an denen
 * der Befund sie verlangt (Vendor code, E-Mail). Am `Link` (Flex-Kind) und an
 * der `body2`-Zeile (`<p>`, Block) ändert sie nichts — dort ist sie schon
 * wirksam. Dieselbe Datei schreibt bei der Sortierzeile ausdrücklich
 * `display: 'block'` für denselben Grund.
 */
const ZELLE_KLEMME_SX = {
  display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function VendorAvatar({ name, logoUrl, size = 32 }) {
  // 🔴 F9 (UI-Durchgang 2, 2026-10-07): ein kaputtes Logo zeigte den Alt-Text
  // („G.M.P.") im Kreis. Ein `onError` schaltet auf dieselbe Initialen-Flaeche
  // um, die ein fehlendes Logo schon bekommt — der Alt-Text ist damit nur noch
  // fuer Hilfsmittel da (`alt=""`: der Name steht direkt daneben in der Zeile,
  // eine zweite Ansage waere eine Dopplung).
  const [logoKaputt, setLogoKaputt] = useState(false)
  const zeigeLogo = !!logoUrl && !logoKaputt
  return (
    <Box sx={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden' }}>
      {zeigeLogo ? (
        <Box component="img" src={logoUrl} alt=""
          onError={() => setLogoKaputt(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Box sx={{
          width: '100%', height: '100%',
          background: (t) => t.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #93c5fd, #3b82f6)'
            : 'linear-gradient(135deg, #2c387e, #1a2358)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: size * 0.34, color: '#fff', lineHeight: 1 }}>
            {getInitials(name)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

/** `Lead 14 d` / `Lead —` — die Zahl ist dieselbe wie in der Zeile, die Einheit darf die Karte tragen. */
const kompakt = (wert) => (wert === null || wert === undefined ? '—' : formatUnitCompact(wert));

/**
 * Unbekannt ist nicht 0 (Regel 22 Nr. 3): fehlt der Wert, steht `—`.
 *
 * 🔴 R16-C1 (2026-10-21): eine STÜCKZAHL hat keine Nachkommastellen. Hier stand
 * `formatNumber`, dessen Vorgabe `digits = 2` ist (`utils/format.js:189`) — die
 * Karte zeigte „15,00 products", die Desktop-Spalte daneben `15`. `formatQty`
 * (`format.js:217`) ist die Hausform für Mengen: ganze Zahl,
 * Tausendertrennzeichen, `—` als Platzhalter.
 */
const zahl = (wert) => formatQty(wert);

export default function VendorsOverview() {
  const [vendors, setVendors] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 🔴 N10.1 (G9a): Suche, Sortierung und Seite leben in der URL — genau EIN
  // Schreiber. Vorher lagen sie in vier `useState`, und ein geteilter Link
  // zeigte nie, was der Absender sah.
  const query = useListQuery({
    dimensions: [{ key: 'q', kind: 'single', label: 'Search' }],
    sort: { default: 'anzeige', values: SORT_VALUES },
    pageSize: ROWS_PER_PAGE,
  });
  const { state, set, clearAll } = query;

  // Die Sortier-Achse traegt `feld` / `-feld` (N10.2); die Tabelle liest daraus
  // Feld und Richtung — dieselbe Umrechnung wie `ProductsTable`.
  const sortFeld = state.sort ?? 'anzeige';
  const sortBy = sortFeld.startsWith('-') ? sortFeld.slice(1) : sortFeld;
  const sortOrder = sortFeld.startsWith('-') ? 'desc' : 'asc';
  // `state.page` ist 1-basiert (N10.4), MUI's Seitenwechsel 0-basiert.
  const seite = state.page - 1;
  const suchbegriff = state.q ?? '';

  // 🔴 Ein gescheiterter Abruf ist NICHT der leere Bestand (R16-A-B1 / R16-B-B1).
  // Vorher wurde jeder Fehler zu `[]` — die Flaeche behauptete danach
  // „No vendors yet.", eine Aussage ueber den Datenbestand, die niemand
  // getroffen hat. `loadError` traegt den Fehler als eigenen Zustand.
  const load = useCallback(() => {
    setLoadError(false);
    setVendors(null);   // zurueck ins Skelett, auch beim Wiederholen
    fetch(`${API}/api/vendors?include_counts=1`)
      .then((res) => {
        // Z-1b: kein roher Statuscode. Die Flaeche zeigt den Text nicht (nur
        // `loadError` zaehlt), aber ein Statuscode in einer geworfenen Meldung
        // ist genau die Form, die der Owner fotografiert hat — und sie landet
        // beim naechsten Griff auf `e.message` sofort im Sichtbaren.
        if (!res.ok) throw new Error('Could not load vendors.');
        return res.json();
      })
      .then((data) => setVendors(Array.isArray(data) ? data : []))
      .catch(() => { setLoadError(true); setVendors([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  // 🔴 E1 / R16 H-4 (2026-10-06): `anzeige` wird EINMAL abgeleitet und traegt
  // Suche, Sortierung UND Anzeige. Wuerde nur die Anzeige ziehen, sortierte die
  // Spalte „Vendor" weiter nach dem Slug und das Suchfeld faende den
  // angezeigten Text nicht mehr — sichtbarer Text und sichtbare Ordnung wuerden
  // auseinanderlaufen. `vendor_name` bleibt durchsuchbar (der Slug ist die
  // Kennung, manche Menschen kennen sie).
  const zeilen = useMemo(
    () => (vendors || []).map((v) => ({ ...v, anzeige: vendorAnzeige(v) })),
    [vendors],
  );

  // 🔴 Die Suche bleibt CLIENTSEITIG und wortgleich (Plan §4.1): der Server-`q`
  // durchsucht nur `vendor_name` und verloere den Anzeigenamen.
  const filtered = useMemo(() => {
    if (!vendors) return [];
    const q = suchbegriff.trim().toLowerCase();
    if (!q) return zeilen;
    return zeilen.filter((v) =>
      [v.anzeige, v.vendor_name, v.contact_name, v.contact_email]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [vendors, zeilen, suchbegriff]);

  const sorted = useMemo(() => stableSort(filtered, getComparator(sortOrder, sortBy)), [filtered, sortOrder, sortBy]);

  const paged = useMemo(() => {
    const start = seite * ROWS_PER_PAGE;
    return sorted.slice(start, start + ROWS_PER_PAGE);
  }, [sorted, seite]);

  // N10.4: der Hook kennt die Zeilenzahl nicht — das Klemmen bleibt beim
  // Aufrufer. Eine Seite hinter dem Ende springt auf die letzte.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sorted.length / ROWS_PER_PAGE) - 1);
    if (seite > maxPage) set('page', maxPage + 1);
  }, [sorted.length, seite, set]);

  const stats = useMemo(() => {
    if (!vendors || vendors.length === 0) return null;
    const totals = vendors.reduce(
      (acc, v) => {
        const products = Number(v.product_count || 0);
        const lead = Number(v.lead_time);
        const ship = Number(v.shipping_time);
        acc.products += Number.isFinite(products) ? products : 0;
        if (Number.isFinite(lead) && lead > 0) {
          acc.lead += lead;
          acc.leadCount += 1;
        }
        if (Number.isFinite(ship) && ship > 0) {
          acc.ship += ship;
          acc.shipCount += 1;
        }
        if (!v.contact_email) acc.missingContact += 1;
        return acc;
      },
      { products: 0, lead: 0, leadCount: 0, ship: 0, shipCount: 0, missingContact: 0 }
    );
    const total = vendors.length;
    return {
      total,
      avgProducts: total ? totals.products / total : 0,
      avgLeadTime: totals.leadCount ? totals.lead / totals.leadCount : 0,
      avgShippingTime: totals.shipCount ? totals.ship / totals.shipCount : 0,
      missingContact: totals.missingContact,
    };
  }, [vendors]);

  const handleRequestSort = (feld) => {
    const istAsc = sortBy === feld && sortOrder === 'asc';
    set('sort', istAsc ? `-${feld}` : feld);
  };

  // 🔴 R16 C1 (2026-10-06): der Kopf steht VOR dem Lade-Zweig und wird in JEDEM Zustand
  // gerendert. Vorher kehrte der Ladezustand in ein Skelett zurueck, das nur graue Balken
  // trug — und die mobile Leiste schweigt seit Befund 5(i): im Laden stand GAR KEIN Titel.
  const kopf = (
    <Box data-testid="liste-kopf">
      <ViewHeader
        title="Vendors"
        // 🔴 F5-4 (UI-Durchgang 2 Teil 5, 2026-10-09): die Unterleiste traegt
        // dieselbe Handlung (`MobileBottomNav` `CREATE_ITEMS`:
        // `New Vendor → /vendors/new`; dieser Knopf geht auf DASSELBE Ziel).
        // Mobil faellt er darum aus dem Kopf. Der Desktop behaelt ihn.
        primaryInBottomNav
        actions={[
          {
            key: 'add',
            label: 'Add vendor',
            variant: 'contained',
            // 🔴 F9 (UI-Durchgang 2, 2026-10-07): „Add vendor" trug kein Zeichen,
            // „New PO" daneben ein `+`. Zwei Anlege-Knoepfe, zwei Formen. Jetzt
            // dieselbe Hausform wie `POOverview.jsx` (`startIcon` am „New PO"-Eintrag).
            startIcon: <AddIcon fontSize="small" />,
            onClick: () => navigate('/vendors/new'),
          },
        ]}
      />
    </Box>
  );

  if (vendors === null) {
    // Skelett statt Spinner — `anti-patterns.md` §11. Es zeichnet die geladene
    // Flaeche nach (KPI-Streifen mit fuenf Kacheln, EINE Suche, sechs Spalten;
    // mobil die Kartenliste), damit die Seite beim Eintreffen der Daten nicht
    // springt (R16-B-W1). Den Kopf traegt `kopf` darueber, nicht mehr das Skelett.
    //
    // `kopf` steht NEBEN dem Skelett, nicht in einem eigenen `PageContainer`: ein
    // `ViewHeader` ausserhalb liefert denselben Rand (`PAGE_GUTTER`) und dieselbe
    // Breite wie einer darin — und das Skelett bringt seinen eigenen
    // `PageContainer` mit. Ein zusaetzlicher hier verschachtelte ihn (E-W5).
    // 🔴 G9a (§2.2 Punkt 1): beide tragen `full` — sonst springt der Rahmen beim
    // Eintreffen der Daten von `reading` auf `full`.
    return (
      <>
        {kopf}
        <VendorsOverviewSkeleton />
      </>
    );
  }

  // 🔴 N8.5 (G9a): der Fehler des ERSTEN Aufbaus sperrt die Bedienelemente —
  // er versteckt sie NICHT. Beide Seiten laden nur einmal je Mount, ein Retry
  // rendert das Skelett; die Unterscheidung „spaeter gescheitertes Auffrischen"
  // beschreibt einen Zustand, den diese Seite nicht hat (Plan §2.2 Punkt 8).
  const sperre = Boolean(loadError);

  const leerGrund = loadError ? 'error' : (vendors.length > 0 ? 'no-match' : 'no-data');

  // Der EINE Leerzustand, an ZWEI Montagepunkten (Desktop im Tabellenkoerper,
  // mobil im Kartenstapel) — eine Quelle, zwei Orte (Plan §2.2 Punkt 7 / B2).
  const leerzustand = (
    <EmptyState
      reason={leerGrund}
      {...(leerGrund === 'no-match'
        ? {
          query: suchbegriff.trim(),
          noun: 'vendor',
          onClearSearch: () => set('q', ''),
        }
        : {})}
      {...(leerGrund === 'no-data' ? { message: 'No vendors yet.' } : {})}
      {...(leerGrund === 'error' ? { message: 'Could not load vendors.' } : {})}
      action={
        leerGrund === 'error' ? (
          <Button variant="outlined" size="small" onClick={load}>Try again</Button>
        ) : leerGrund === 'no-data' ? (
          <Button variant="outlined" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => navigate('/vendors/new')}>
            Add vendor
          </Button>
        ) : undefined
      }
    />
  );

  // 🔴 W1 (Plan §2.2 Punkt 5): `unknownTotal` NUR im ungefilterten Zustand —
  // nach einer Suche ist `n` die exakte Trefferzahl, und „3+" waere falsch.
  // Im Fehler des ersten Aufbaus traegt der Zaehler NICHTS (`n = null`): eine 0
  // waere die Behauptung „null Lieferanten", wo „konnte nicht laden" gilt.
  const zaehler = sperre
    ? { noun: 'vendors', n: null, m: null }
    : {
      noun: 'vendors',
      n: sorted.length,
      m: vendors.length,
      unknownTotal: sorted.length === vendors.length && vendors.length >= 200,
    };

  const sortLabel = (SORT_OPTIONS.find((o) => o.value === sortFeld) || SORT_OPTIONS[0]).label;

  const zeileLink = (vendor) => (
    <MuiLink
      component={RouterLink}
      to={`/vendors/${vendor.id}`}
      underline="hover"
      color="inherit"
      // R16-W2 = R15-W3: der Lieferantenname ist der längste Text der Zeile —
      // in fester Satzart kürzt er jetzt statt in die Nachbarzelle zu laufen.
      // `minWidth: 0` ist nötig, weil der Link in einem Flex-Elternteil sitzt.
      title={vendor.anzeige || undefined}
      sx={{ fontWeight: 600, minWidth: 0, ...ZELLE_KLEMME_SX }}
      // 🔴 N9.9: der Link ist der Tastaturweg; die Zeile oeffnet per Klick.
      // Ohne `stopPropagation` navigierte der Klick ZWEIMAL (Link + Zeile).
      onClick={(e) => e.stopPropagation()}
    >
      {vendor.anzeige || '—'}
    </MuiLink>
  );

  const chips = (vendor) => (
    vendor.rma_config ? (
      <>
        {/* R16-C2: dieselbe Hausform wie in der Desktop-Zelle — die
            Prozessart ist ein Etikett, kein Satz. */}
        <Pill tone={vendor.rma_config.active ? 'ok' : 'plain'} nowrap>RMA</Pill>
        <Pill tone={vendor.rma_config.active ? 'info' : 'plain'} nowrap>
          {String(vendor.rma_config.process_type || '—').replace(/_/g, ' ')}
        </Pill>
      </>
    ) : null
  );

  const meta = (vendor) => (
    <>
      <Typography variant="body2" color="text.secondary">
        {vendor.contact_name || '—'} · {vendor.contact_email || '—'}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        Lead {kompakt(vendor.lead_time)} · Ship {kompakt(vendor.shipping_time)} ·{' '}
        {/* 🔴 R16-Runde-2 W4 (2026-10-21): der Numerus folgt dem Wert. Hier
            stand fest „products", und das Foto zeigte „1 products" (EQUA
            d.o.o.). `Number(...)`, damit auch „1" aus JSON trifft. */}
        {zahl(vendor.product_count)} {Number(vendor.product_count) === 1 ? 'product' : 'products'}
      </Typography>
    </>
  );

  return (
    <PageContainer width="full">
      {kopf}

      {stats && (
        <Box data-testid="liste-kpi">
          <KpiRow sx={{ mb: 3 }}>
            <KpiCell>
              <StatTile label="Total vendors" value={stats.total} />
            </KpiCell>
            <KpiCell>
              <StatTile label="Avg products / vendor" value={formatNumber(stats.avgProducts, { digits: 1 })} hint="items" />
            </KpiCell>
            <KpiCell>
              <StatTile label="Avg lead time" value={formatUnit(stats.avgLeadTime, 'd', { digits: 1 })} />
            </KpiCell>
            <KpiCell>
              <StatTile label="Avg shipping time" value={formatUnit(stats.avgShippingTime, 'd', { digits: 1 })} />
            </KpiCell>
            <KpiCell>
              <StatTile label="Vendors missing contact" value={stats.missingContact} />
            </KpiCell>
          </KpiRow>
        </Box>
      )}

      <Panel data-testid="liste-panel" sx={{ mb: SP.md }}>
        {/* N8.5: der GRUND der Sperre steht sichtbar DIREKT ueber der Leiste und
            sagt etwas anderes als der Leerzustand — der `EmptyState` nennt, WAS
            fehlschlug, dieser Satz nennt, WARUM die Bedienelemente gesperrt sind. */}
        {/* 🔴 R16 B1 (G9b-Nachbesserung 1, 2026-10-22): MUI setzt an einem
            `Alert` ohne `role` ein ASSERTIVES `role="alert"` — und der
            Fehler-Leerzustand darunter ist ebenfalls `role="alert"`
            (`EmptyState.jsx`, `reason === 'error'`). Damit sagte der Sperrhinweis
            (die FOLGE) assertiv vor der Ursache an. `role="status"` stuft ihn ab,
            der Wortlaut bleibt. Hausform `ScraperList.jsx:2132-2146`; die
            Schwesterliste `/competitors` traegt denselben Fix. */}
        {sperre && (
          <Alert
            severity="warning"
            role="status"
            data-testid="erster-aufbau-gesperrt"
            sx={{ mb: SP.card }}
          >
            {/* 🔴 R16 W3 (G9b-Nachbesserung 4, 2026-10-22): der zweite Satz
                („Use Try again below.") ist gestrichen — genau wie auf
                `/competitors` (W6 aus Runde 1). Er zeigte ins Leere, sobald
                `EmptyState` im BESTAETIGTEN Ausfall schweigt (Z-1a): dann stand
                dort ein Verweis auf einen Knopf, den es nicht gibt. Zwei
                Wortlaute fuer dieselbe Lage waren der Fehler. */}
            Search and filters are locked until the list loads.
          </Alert>
        )}

        {/* N10.4: ein verworfener URL-Wert wird EINMAL gemeldet, nicht still
            verschluckt. Hausform (fuenf handgerollte `Alert`s im Repo, kein
            gemeinsames Bauteil — Plan §3.2/W11). */}
        {query.ignoredValues.length > 0 && (
          <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mb: SP.card }}>
            {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
          </Alert>
        )}

        <ListToolbar
          shortcut="/"
          search={{
            value: suchbegriff,
            onChange: (v) => set('q', v),
            onCommit: (v) => set('q', v),
            placeholder: 'Search vendors…',
            // P5: der zugaengliche Name beginnt mit „Search".
            label: 'Search vendors',
            // P5/P34: die Rolle, nicht nur der Platzhalter.
            inputRole: 'searchbox',
            disabled: sperre,
            // N2.9: die Suche fuellt den Rest von Z1 (`SearchTrigger` reicht
            // `sx` an das Wurzelelement weiter — ohne `flex` blieb sie auf ihrer
            // Eigenbreite, und der Abstand zur naechsten Flaeche war zu gross).
            sx: { flex: 1, minWidth: 0 },
          }}
          count={zaehler}
          // 🔴 BS6 NUR unter `sm` (Plan §2.1): der Desktop traegt die Sortierung
          // im Tabellenkopf; ein zweiter Sortierweg dort waere eine Doppelquelle.
          // `criteria: []` — die Karte gibt dieser Liste keine Facette.
          moreFilters={isMobile ? {
            disabled: sperre,
            criteria: [],
            sort: {
              value: sortFeld,
              onChange: (v) => set('sort', v),
              options: SORT_OPTIONS,
            },
            // §4.2: im Fehler des ersten Aufbaus traegt das Blatt keinen Zaehler.
            resultCount: sperre ? undefined : sorted.length,
            // Sortieren ist kein Filter (N4.6).
            activeCount: 0,
            // 🔴 B4: ohne `onClearAll` rendert `FilterSheet` KEIN „Clear all".
            onClearAll: clearAll,
            // 🔴 R16-W3 (2026-10-21): das Blatt hat auf dieser Liste KEINE
            // Facette (`criteria: []`) — es enthält nur „Sort by", und „Filters"
            // verspricht eine Fläche, die es nicht gibt. `FilterSheet` trägt
            // `label`/`title` als Props, ein Umbenennen an dieser Stelle wäre
            // aber KEINE Einzeiler: der Hausselektor
            // `button[aria-label^="Filters"]` (`design/muster/selektoren.js:72`)
            // und zwei Helferproben (`messeP16`, `messeP9Sperre`, beide für alle
            // vierzehn Karten) hängen am Literal. Deshalb als eigener Schritt
            // abgelegt — Backlog-Karte
            // `docs/backlog-auto-repair/2026-10-21-filters-knopf-oeffnet-nur-sortierung.md`.
            label: 'Filters',
            title: 'Filters',
          } : undefined}
          query={{ chips: query.chips, clearAll }}
        >
          {/* Mobil: der EINE Sortiersatz unter der Leiste. Der Desktop traegt
              die Ordnung im Tabellenkopf (`aria-sort`) — ein zweiter Weg auf
              derselben Breite waere einer zu viel (Plan §2.2 Punkt 9). */}
          {isMobile && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="sortiert-nach"
              sx={{ display: 'block', mt: SP.tight }}
            >
              {`Sorted by ${sortLabel}.`}
            </Typography>
          )}

          {isMobile ? (
            /* ─── Mobil: Kartenstapel ─── */
            <Stack data-testid="liste-karte" spacing={0} sx={{ mt: SP.tight }}>
              {sorted.length === 0 && leerzustand}
              {paged.map((vendor) => (
                <MobileRecordCard
                  key={vendor.id}
                  leading={<VendorAvatar name={vendor.anzeige} logoUrl={vendor.logo_url} />}
                  title={<MuiLink component={RouterLink} to={`/vendors/${vendor.id}`} underline="hover" sx={KARTE_TITEL_SX} onClick={(e) => e.stopPropagation()}>{vendor.anzeige || '—'}</MuiLink>}
                  // 🔴 W9: der Slot wird IMMER gerendert — fehlt `vendor_sku`,
                  // steht dort `—`. Unbekannt ist nicht 0, und der Anker fuer
                  // P2/P20/P15 bleibt.
                  id={`Vendor code ${vendor.vendor_sku || '—'}`}
                  chips={chips(vendor)}
                  meta={meta(vendor)}
                  slotOrder="chips-first"
                  fieldTestIds={{
                    title: 'karte-titel-vendor',
                    id: 'karte-kennung-vendor',
                    meta: 'karte-meta-vendor',
                    chips: 'karte-chips-vendor',
                  }}
                  onClick={() => navigate(`/vendors/${vendor.id}`)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          ) : (
            /* ─── Desktop: Tabelle ─── */
            <Box data-testid="liste-tabelle" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {SORT_FELDER.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        // 🔴 B7: `aria-sort` kommt aus `TableCell.sortDirection`;
                        // `TableSortLabel` allein stempelt es NICHT.
                        sortDirection={headCell.sortierbar !== false && sortBy === headCell.id ? sortOrder : false}
                        // 🔴 R15-W2: KEIN `nowrap` mehr. In fester Satzart
                        // weitet der Browser die Spalte nicht — bei `minWidth:
                        // 900` sind 14 % nur 126 px, „Lead Time (days)" braucht
                        // gemessen 150. Mit `nowrap` lief der Text in die
                        // Nachbarzelle; ohne bricht er um. `title` hält das
                        // Etikett auch dann lesbar, wenn es zweizeilig steht.
                        title={headCell.label}
                        sx={{ width: headCell.width }}
                      >
                        {headCell.sortierbar === false ? (
                          headCell.label
                        ) : (
                          <TableSortLabel
                            active={sortBy === headCell.id}
                            direction={sortBy === headCell.id ? sortOrder : 'asc'}
                            onClick={() => handleRequestSort(headCell.id)}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={SORT_FELDER.length} sx={{ border: 0 }}>
                        {leerzustand}
                      </TableCell>
                    </TableRow>
                  )}
                  {paged.map((vendor) => (
                    <TableRow
                      key={vendor.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/vendors/${vendor.id}`)}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          {/* E1 / R16 H-4: die Initialen kommen aus demselben
                              Namen, der in der Zeile steht — sonst traegt der
                              Kreis „M" (moins) neben dem Text „Moins Coffee". */}
                          <VendorAvatar name={vendor.anzeige} logoUrl={vendor.logo_url} />
                          {/* `minWidth: 0` + `flex: 1`: ohne beides wüchse der
                              Inhalt über die Zelle hinaus, statt zu kürzen
                              (R16-W2 = R15-W3). */}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                              {zeileLink(vendor)}
                              {vendor.rma_config && (
                                <Pill tone={vendor.rma_config.active ? 'ok' : 'plain'} nowrap>RMA</Pill>
                              )}
                            </Stack>
                            {/* 🔴 F9: die graue Kleinzahl unter dem Namen ist
                                `vendor_sku` — der Lieferantencode. Dieselbe
                                Groesse heisst im Lieferantendetail „Vendor code". */}
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              title={`Vendor code ${vendor.vendor_sku || '—'}`}
                              sx={ZELLE_KLEMME_SX}
                            >
                              Vendor code{' '}
                              <Box component="span" sx={{ fontFamily: 'monospace' }}>{vendor.vendor_sku || '—'}</Box>
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          title={vendor.contact_name || undefined}
                          sx={ZELLE_KLEMME_SX}
                        >
                          {vendor.contact_name || '—'}
                        </Typography>
                        {/* R16-W2 = R15-W3: die E-Mail ist die Stelle, die der
                            Bericht namentlich nennt — 19 % sind bei 1082 px
                            rund 205 px, eine längere Adresse lief bisher in
                            die Nachbarzelle. */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          title={vendor.contact_email || undefined}
                          sx={ZELLE_KLEMME_SX}
                        >
                          {vendor.contact_email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{vendor.lead_time ?? '—'}</TableCell>
                      <TableCell align="right">{vendor.shipping_time ?? '—'}</TableCell>
                      {/* R16-C1: dieselbe Größe wie auf der Karte — ganze Zahl,
                          nie „15,00". */}
                      <TableCell align="right">{formatQty(vendor.product_count)}</TableCell>
                      <TableCell>
                        {vendor.rma_config ? (
                          // R16-C2: `nowrap` an der Aufrufstelle, nicht am
                          // Baustein (seine Vorgabe bleibt „darf umbrechen").
                          <Pill tone={vendor.rma_config.active ? 'info' : 'plain'} nowrap>
                            {String(vendor.rma_config.process_type || '—').replace(/_/g, ' ')}
                          </Pill>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* 🔴 B8 (N8.7): die Marke steht NUR mit Inhalt. `TableFooterSimple`
              gibt nur bei `count === 0` `null` zurueck; ohne `leftSlot` und ohne
              Seitenwechsel bliebe ein leerer 44-px-Streifen stehen. Geblaettert
              wird erst ueber 50 Zeilen. */}
          {sorted.length > ROWS_PER_PAGE && (
            <Box data-testid="liste-fuss">
              <TableFooterSimple
                count={sorted.length}
                page={seite}
                onPageChange={(p) => set('page', p + 1)}
                rowsPerPage={ROWS_PER_PAGE}
                // 🔴 BS17: mobil steht die Leiste im FLUSS statt zu schweben —
                // die schwebende Fassung deckt die Kartenliste zu (F5-1).
                stickyOnMobile={false}
              />
            </Box>
          )}
        </ListToolbar>
      </Panel>
    </PageContainer>
  );
}
